import React from 'react';
import { SquarePen, TriangleAlert } from 'lucide-react';
import { Modal } from '../../../components/common/Modal';
import { useLanguage } from '../../../features/language/useLanguage';
import type { WasteRow } from '../hooks/useWasteManagement';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  rows: WasteRow[];
  setRows: React.Dispatch<React.SetStateAction<WasteRow[]>>;
  error: string | null;
  submitting: boolean;
  editingKey: string | null;
  setEditingKey: (key: string | null) => void;
  onConfirm: () => void | Promise<void>;
  roastTypeLabelMap: Record<string, string>;
}

const isManuallyAdjusted = (row: WasteRow) =>
  Math.abs(Number(row.remainingKg || 0) - Number(row.systemRemainingKg || 0)) > 0.0005;

export const WasteManagementModal: React.FC<Props> = ({
  isOpen,
  onClose,
  rows,
  setRows,
  error,
  submitting,
  editingKey,
  setEditingKey,
  onConfirm,
  roastTypeLabelMap,
}) => {
  const { t } = useLanguage();

  const updateRow = (itemId: string, roastType: string, updater: (row: WasteRow) => WasteRow) =>
    setRows((prev) => prev.map((r) => (r.itemId === itemId && r.roastType === roastType ? updater(r) : r)));

  const commitEdit = (row: WasteRow) =>
    updateRow(row.itemId, row.roastType, (r) => {
      const parsed = r.remainingInput === '' ? NaN : Number(r.remainingInput);
      const next = Number.isFinite(parsed) ? Math.max(0, parsed) : r.systemRemainingKg;
      return { ...r, remainingKg: next, remainingInput: next.toFixed(3) };
    });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('workOrders.waste.title')} maxWidth="4xl">
      <div className="space-y-4">
        {error && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700" data-test-id="waste-management-error">
            {error}
          </div>
        )}
        <div className="rounded-md border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-12 gap-3 bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-600">
            <div className="col-span-6">{t('workOrders.waste.table.item')}</div>
            <div className="col-span-3 text-right">{t('workOrders.waste.table.remainingKg')}</div>
            <div className="col-span-3 text-right">{t('workOrders.waste.table.action')}</div>
          </div>
          <div className="divide-y divide-gray-200">
            {rows.map((row) => {
              const rowKey = `${row.itemId}:${row.roastType}`;
              const adjusted = isManuallyAdjusted(row);
              return (
                <div
                  key={rowKey}
                  className="grid grid-cols-12 gap-3 items-center px-4 py-3 text-sm"
                  data-test-id={`waste-management-row-${row.itemId}-${row.roastType}`}
                >
                  <div className="col-span-6">
                    <div className="font-medium text-gray-900">
                      {row.itemName}
                      {row.itemCode ? ` (${row.itemCode})` : ''}
                    </div>
                    <div className="text-xs text-gray-500">{roastTypeLabelMap[row.roastType] || row.roastType}</div>
                  </div>
                  <div className="col-span-3 flex flex-col items-end">
                    <div className="flex items-center gap-2 group">
                      {editingKey === rowKey ? (
                        <input
                          autoFocus
                          type="number"
                          step={0.001}
                          min={0}
                          value={row.remainingInput}
                          onChange={(event) => {
                            const raw = event.target.value;
                            updateRow(row.itemId, row.roastType, (r) => {
                              const parsed = raw === '' ? NaN : Number(raw);
                              return {
                                ...r,
                                remainingInput: raw,
                                remainingKg: Number.isFinite(parsed) ? parsed : r.remainingKg,
                              };
                            });
                          }}
                          onKeyDown={(event) => {
                            if (event.key !== 'Enter') return;
                            commitEdit(row);
                            setEditingKey(null);
                          }}
                          onBlur={() => {
                            commitEdit(row);
                            setEditingKey(null);
                          }}
                          className={`w-24 border-b bg-transparent text-right text-sm font-semibold outline-none ${
                            adjusted ? 'border-amber-500 text-amber-900' : 'border-blue-500 text-gray-900'
                          }`}
                          data-test-id={`waste-management-remaining-input-${row.itemId}-${row.roastType}`}
                        />
                      ) : (
                        <>
                          <span
                            className={`text-sm font-bold ${adjusted ? 'text-amber-900' : 'text-gray-900'}`}
                            data-test-id={`waste-management-remaining-text-${row.itemId}-${row.roastType}`}
                          >
                            {Number(row.remainingKg || 0).toFixed(3)}
                          </span>
                          <button
                            type="button"
                            onClick={() => setEditingKey(rowKey)}
                            className="text-gray-400 hover:text-blue-600 transition-colors"
                            data-test-id={`waste-management-remaining-edit-${row.itemId}-${row.roastType}`}
                          >
                            <SquarePen className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                    {adjusted && (
                      <div
                        className="mt-1 flex items-center justify-end gap-1 text-[11px] font-medium text-amber-800"
                        data-test-id={`waste-management-remaining-warning-${row.itemId}-${row.roastType}`}
                      >
                        <TriangleAlert className="h-3.5 w-3.5" />
                        <span>
                          {t('workOrders.waste.manualLabel')}: {Number(row.systemRemainingKg || 0).toFixed(3)} → {Number(row.remainingKg || 0).toFixed(3)} (
                          {Number(row.remainingKg || 0) - Number(row.systemRemainingKg || 0) >= 0 ? '+' : ''}
                          {(Number(row.remainingKg || 0) - Number(row.systemRemainingKg || 0)).toFixed(3)})
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="col-span-3 flex justify-end ">
                    <div className="bg-gray-200 w-fit p-1 rounded-md">
                      <button
                        type="button"
                        onClick={() => updateRow(row.itemId, row.roastType, (r) => ({ ...r, action: 'keep' }))}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-md ${
                          row.action === 'keep' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        data-test-id={`waste-management-keep-${row.itemId}-${row.roastType}`}
                      >
                        {t('workOrders.waste.actions.keep')}
                      </button>
                      <button
                        type="button"
                        onClick={() => updateRow(row.itemId, row.roastType, (r) => ({ ...r, action: 'waste' }))}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-md ${
                          row.action === 'waste' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        data-test-id={`waste-management-waste-${row.itemId}-${row.roastType}`}
                      >
                        {t('workOrders.waste.actions.waste')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            {rows.length === 0 && (
              <div className="px-4 py-6 text-center text-sm text-gray-500">{t('workOrders.waste.empty')}</div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => void onConfirm()}
            disabled={submitting || rows.length === 0}
            className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-2 py-1 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            data-test-id="waste-management-confirm"
          >
            {submitting ? t('workOrders.waste.processing') : t('workOrders.waste.actions.confirm')}
          </button>
        </div>
      </div>
    </Modal>
  );
};
